import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useSubscription } from "../context/SubscriptionContext";
import { CheckCircle2, Loader2, ArrowRight } from "lucide-react";

export default function PaymentSuccess() {
  const { search } = useLocation();
  const navigate = useNavigate();
  const { refresh } = useSubscription();
  const [status, setStatus] = useState("pending"); // pending | paid | error | timeout
  const attempts = useRef(0);

  useEffect(() => {
    const params = new URLSearchParams(search);
    const sessionId = params.get("session_id");
    if (!sessionId) {
      setStatus("error");
      return;
    }

    let cancelled = false;
    const poll = async () => {
      if (cancelled) return;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setStatus("paid");
          await refresh();
          return;
        }
        if (data.status === "expired" || data.payment_status === "failed") {
          setStatus("error");
          return;
        }
      } catch (err) {
        if (err.response?.status === 404) {
          setStatus("error");
          return;
        }
        // transient network error – keep polling
      }
      attempts.current += 1;
      if (attempts.current >= 20) {
        setStatus("timeout");
        return;
      }
      setTimeout(poll, 2000);
    };
    poll();
    return () => { cancelled = true; };
  }, [search, refresh]);

  return (
    <div className="min-h-screen bg-white flex items-center justify-center px-6">
      <div className="max-w-md w-full text-center ss-fadein" data-testid="payment-success-view">
        {status === "pending" && (
          <>
            <Loader2 className="w-12 h-12 text-yellow-500 animate-spin mx-auto" />
            <h1 className="mt-6 text-2xl font-extrabold tracking-tight text-slate-900">Weryfikujemy płatność...</h1>
            <p className="mt-2 text-slate-500 text-sm">To potrwa chwilę. Nie zamykaj okna.</p>
          </>
        )}
        {status === "paid" && (
          <>
            <div className="w-16 h-16 mx-auto rounded-3xl bg-green-50 border border-green-200 flex items-center justify-center">
              <CheckCircle2 className="w-9 h-9 text-green-600" />
            </div>
            <h1 className="mt-6 text-3xl font-extrabold tracking-tight text-slate-900">Płatność zaksięgowana!</h1>
            <p className="mt-2 text-slate-600">Twoja subskrypcja jest aktywna. Miłej nauki.</p>
            <button
              data-testid="go-home-button"
              onClick={() => navigate("/", { replace: true })}
              className="mt-8 inline-flex items-center gap-2 bg-[#FFD600] hover:bg-[#E6C200] text-slate-900 font-bold px-6 py-3.5 rounded-2xl btn-yellow-glow"
            >
              Wróć do aplikacji <ArrowRight className="w-4 h-4" strokeWidth={2.5} />
            </button>
          </>
        )}
        {status === "error" && (
          <>
            <h1 className="text-2xl font-extrabold tracking-tight text-slate-900">Coś poszło nie tak.</h1>
            <p className="mt-2 text-slate-500 text-sm">Płatność nie została zakończona.</p>
            <button
              onClick={() => navigate("/", { replace: true })}
              className="mt-6 inline-flex items-center gap-2 bg-slate-900 text-white px-6 py-3 rounded-2xl font-bold"
            >
              Wróć do aplikacji
            </button>
          </>
        )}
        {status === "timeout" && (
          <>
            <h1 className="text-2xl font-extrabold tracking-tight text-slate-900">Weryfikacja trwa dłużej niż zwykle.</h1>
            <p className="mt-2 text-slate-500 text-sm">Sprawdź za chwilę — status subskrypcji odświeży się automatycznie.</p>
            <button
              onClick={() => navigate("/", { replace: true })}
              className="mt-6 inline-flex items-center gap-2 bg-slate-900 text-white px-6 py-3 rounded-2xl font-bold"
            >
              Wróć do aplikacji
            </button>
          </>
        )}
      </div>
    </div>
  );
}
